import { Browser } from '@capacitor/browser';
import { Button } from '@powerful/ui';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import symbolGold from '@powerful/brand/assets/logo/powerful-connect-symbol-gold.svg';

import type { GatePlatform } from '@powerful/data';

/** URLs das lojas. Substituídas pelos IDs reais quando as fichas existirem (T7). */
const STORE_URL: Record<GatePlatform, string> = {
  ios: 'https://apps.apple.com/app/powerful-connect/id0000000000',
  android: 'https://play.google.com/store/apps/details?id=pt.powerfulconnect.app',
  web: 'https://powerfulconnect.pt',
};

export interface ForceUpdateScreenProps {
  platform: GatePlatform;
  /** Chave i18n vinda de `app_settings`; permite trocar a mensagem sem publicar build. */
  messageKey?: string | null;
}

/**
 * Ecrã bloqueante de atualização obrigatória (PRD §10.15).
 *
 * Fundo `--bg` dark fixo: não respeita o seletor de tema porque o app não chegou a carregar a
 * preferência do usuário — este ecrã aparece antes de qualquer navegação.
 *
 * Não há saída. Sem botão de fechar, sem gesto de voltar, sem deep link que contorne: um build
 * que já não consegue ler o schema não pode alcançar nenhuma funcionalidade.
 */
export function ForceUpdateScreen({ platform, messageKey }: ForceUpdateScreenProps) {
  const { t } = useTranslation();
  const titleId = useId();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const ctaRef = useRef<HTMLButtonElement>(null);

  const baseKey = messageKey ?? 'update.required';

  // O leitor de ecrã precisa começar pelo motivo do bloqueio, não pelo botão da loja.
  useEffect(() => {
    titleRef.current?.focus();
  }, []);

  // Prende o foco. O diálogo tem um único controlo focável, então Tab e Shift+Tab convergem
  // sempre para ele — é o que impede navegar por baixo do ecrã com o teclado.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      event.preventDefault();
      ctaRef.current?.focus();
    }

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-[color:var(--bg)] px-6 text-center"
    >
      <img src={symbolGold} alt="" aria-hidden="true" className="h-20 w-20" />

      <h1
        id={titleId}
        ref={titleRef}
        tabIndex={-1}
        className="font-display text-3xl text-[color:var(--text)] outline-none"
      >
        {t(`${baseKey}.title`)}
      </h1>

      <p className="max-w-sm font-sans text-base text-[color:var(--text-muted)]">
        {t(`${baseKey}.body`)}
      </p>

      <Button
        ref={ctaRef}
        variant="metal"
        size="lg"
        onClick={() => {
          void Browser.open({ url: STORE_URL[platform] });
        }}
      >
        {t(`${baseKey}.cta`)}
      </Button>
    </div>
  );
}
